import { ComponentChildren, Ref } from 'preact';
import { useAccessibleContext } from '../hooks/UNSAFE_useAccessibleContext';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { classNames } from '../utils/UNSAFE_classNames';
import { textInterpolations } from '../utils/UNSAFE_interpolations/text';
import type { TextProps } from '../utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import {
  ReadonlyTextFieldInputVariantOptions,
  ReadonlyTextFieldInputStyles
} from './themes/ReadonlyTextFieldInputStyles.css';
import { ReadonlyTextFieldInputRedwoodTheme } from './themes/redwood/ReadonlyTextFieldInputTheme';
import { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';

type AutoFocusProp = HTMLAttributesSignalExcluded<HTMLInputElement>['autofocus'];

type ReadonlyAsInputProps = {
  as: 'input';
  type: HTMLAttributesSignalExcluded<HTMLInputElement>['type'];
  elementRef?: Ref<HTMLInputElement>;
  rows?: never;
};

type ReadonlyAsTextAreaProps = {
  as: 'textarea';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: number;
};

type ReadonlyAsDivProps = {
  as?: 'div';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: never;
};

type Props = TextProps &
  (ReadonlyAsDivProps | ReadonlyAsInputProps | ReadonlyAsTextAreaProps) & {
    'aria-describedby'?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    autoFocus?: AutoFocusProp;
    hasEmptyLabel?: boolean;
    hasInsideLabel?: boolean;
    inlineUserAssistance?: ComponentChildren;
    innerReadonlyField?: ComponentChildren;
    value?: string;
    variant?: 'textarea';
  };

/**
 * Merge multiple ids into a space-separated list
 * @param ids The ids to be merged
 * @returns The space separated string of ids
 */
function mergeIDs(...ids: (string | undefined)[]) {
  return ids.filter(Boolean).join(' ') || undefined;
}

export function ReadonlyTextFieldInput({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': inputLabelledBy,
  autoFocus,
  as = 'div',
  elementRef,
  hasEmptyLabel,
  hasInsideLabel = false,
  inlineUserAssistance,
  innerReadonlyField,
  rows,
  type,
  value = '',
  variant,
  ...props
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const loadingAriaLabel = translations.formControl_loading();
  const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
  const { isLoading } = useFormFieldContext();
  const ariaLabelForReadonly = isLoading ? loadingAriaLabel : ariaLabel;

  // get classes for text align
  const interpolations = [...Object.values(textInterpolations)];
  const styleInterpolations = mergeInterpolations<Props>(interpolations);
  const { class: styleInterpolationClasses } = styleInterpolations(props);
  const { classes: themeClasses } = useComponentTheme<
    ReadonlyTextFieldInputVariantOptions,
    ReadonlyTextFieldInputStyles
  >(ReadonlyTextFieldInputRedwoodTheme, {
    textarea: variant === 'textarea' ? 'isTextArea' : 'notTextArea',
    formLayout: isFormLayout ? 'isFormLayout' : 'notFormLayout',
    readonlyForm: isReadonlyForm ? 'isReadonlyForm' : 'notReadonlyForm',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel'
  });

  const readonlyDivClasses = classNames([themeClasses, styleInterpolationClasses]);

  // JET-52914 - Implement Tabbable mode API contract in InputText
  // spread tabbableModeProps on component
  const { isTabbable, tabbableModeProps } = useTabbableMode();

  // JET-52089: add unsafe API for specifying an external label which is added to ariaLabelledBy on the input or div,
  // only when labelEdge is 'none' and label is ''. Since ariaLabelledBy takes precedence over all other
  // kinds of labels, this helps to ensure we don't override a meaningful label.
  const { UNSAFE_ariaLabelledBy } = useAccessibleContext();
  const ariaLabelledBy = hasEmptyLabel
    ? mergeIDs(inputLabelledBy, UNSAFE_ariaLabelledBy)
    : inputLabelledBy;

  if (as === 'input') {
    return (
      <input
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-ignore
        ref={elementRef}
        type={type}
        value={value}
        {...(!isTabbable && tabbableModeProps)}
      />
    );
  }
  if (as === 'textarea') {
    return (
      <textarea
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-ignore
        ref={elementRef}
        rows={rows}
        {...(!isTabbable && tabbableModeProps)}>
        {value}
      </textarea>
    );
  }
  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabelForReadonly}
      aria-labelledby={ariaLabelledBy}
      aria-readonly={true}
      autofocus={autoFocus}
      class={readonlyDivClasses}
      // @ts-ignore
      ref={elementRef}
      role="textbox"
      {...tabbableModeProps}>
      {value}
    </div>
  );
}
